import { useMemo, useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { usePermissions } from '@/shared/permissions';
import { useTenant } from '@/shared/tenant/useTenant';
import { EmptyState, SectionScaffold } from '@/shared/ui';

/**
 * Products section — the catalog workspace for the active tenant.
 *
 * The product list (search + table) will render here once the catalog data API
 * is wired; today it surfaces the configuration entry point so merchants can set
 * up catalog behaviour. Configuration lives behind the header gear.
 */
export function ProductsPage() {
  const { hasPermission } = usePermissions();
  const { tenantName } = useTenant();
  const [search, setSearch] = useState('');

  const canView = hasPermission('settings.catalog.view');
  const tenantLabel = useMemo(() => tenantName ?? 'this store', [tenantName]);

  return (
    <SectionScaffold
      title="Products"
      subtitle="Browse, search and manage the product catalog for this store."
      canView={canView}
      settingsTo="/settings/catalog"
      settingsLabel="Catalog settings"
      search={{
        value: search,
        onChange: setSearch,
        placeholder: 'Search products by name or SKU…',
        disabled: true,
        disabledHint: 'Product search becomes available once the catalog is connected.',
      }}
      primaryAction={
        <Button variant="contained" startIcon={<AddRoundedIcon />} disabled>
          Add product
        </Button>
      }
    >
      <EmptyState
        icon={<Inventory2RoundedIcon sx={{ fontSize: 48 }} />}
        title="Product management is coming soon"
        description={`The product catalog for ${tenantLabel} will appear here. In the meantime, configure how your catalog behaves — publication, visibility, search and SEO defaults.`}
        action={
          <Button
            variant="outlined"
            component={RouterLink}
            to="/settings/catalog"
            startIcon={<SettingsRoundedIcon />}
          >
            Open catalog settings
          </Button>
        }
      />
    </SectionScaffold>
  );
}
